/**
 * A snapshot encoded once, whoever it goes to.
 *
 * Every tick the players' list goes to every player, each without their
 * own entry in it, and the herd goes to each with only what is near them.
 * Built as objects and handed to each link, every one was turned into
 * text again - twenty players, twenty times the same forty animals
 * through JSON.stringify, fifteen times a second, on one core.
 *
 * So each item is written once, as a piece of text, and a message for a
 * given player is those pieces joined with the punctuation JSON would
 * have put between them. The object is still built beside the text, for
 * a link that never leaves this side and wants the thing rather than
 * its spelling. The one rule is that the text is what JSON.stringify
 * would have written for the object, character for character, and a
 * check holds that rule.
 */

export interface Piece<T> {
  readonly item: T;
  readonly text: string;
}

/** Write each item once. */
export function pieces<T>(items: readonly T[]): Piece<T>[] {
  return items.map((item) => ({ item, text: JSON.stringify(item) }));
}

/** A field of a snapshot: its name, and the pieces kept for this reader. */
export type Field = readonly [name: string, kept: readonly Piece<unknown>[]];

/** The message, as an object, for a link that stays on this side. */
export function snapshotObject(t: string, fields: readonly Field[]): object {
  const out: Record<string, unknown> = { t };
  for (const [name, kept] of fields) out[name] = kept.map((p) => p.item);
  return out;
}

/** The same message, as the text JSON.stringify would have written. */
export function snapshotText(t: string, fields: readonly Field[]): string {
  let out = `{"t":${JSON.stringify(t)}`;
  for (const [name, kept] of fields) {
    out += `,${JSON.stringify(name)}:[`;
    for (let i = 0; i < kept.length; i++) {
      if (i > 0) out += ",";
      out += kept[i]!.text;
    }
    out += "]";
  }
  return out + "}";
}
